import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import DateInput from '../../components/DateInput/DateInput';
import { urlApiProd } from '../utiles';

const TIPO_CONTENEDOR = '40HC Supertestado';

const MAX_RESULTADOS = 30;

const libreDe = (pi) => Math.max((pi.hc ?? 0) - (pi.hc_en_solicitudes ?? 0), 0);

// idProformaInicial: PI que llega preseleccionada (por ejemplo, desde la alerta "PI sin booking")
const NuevaSolicitudInterna = ({ cerrar, onGuardada, idProformaInicial = null }) => {
    const [datos, setDatos] = useState({ pis: [], puertos: [], internas: [] });
    const [cargando, setCargando] = useState(true);
    const [errorCarga, setErrorCarga] = useState('');
    const [errorGuardado, setErrorGuardado] = useState('');
    const [guardando, setGuardando] = useState(false);

    const [busqueda, setBusqueda] = useState('');
    const [listaAbierta, setListaAbierta] = useState(false);
    const [seleccion, setSeleccion] = useState([]); // [{ id_proforma, hc: '10' }]
    const [etd, setEtd] = useState('');
    const [etdEditado, setEtdEditado] = useState(false);
    const [freeTime, setFreeTime] = useState('');
    const [freeTimeEditado, setFreeTimeEditado] = useState(false);
    const [tipoBl, setTipoBl] = useState('BL Original');
    const [requerimientos, setRequerimientos] = useState('');
    const inputBusqueda = useRef(null);

    useEffect(() => {
        let activo = true;
        const cargar = async () => {
            const token = localStorage.getItem('token');
            const encabezados = { headers: { Authorization: `Bearer ${token}` } };
            try {
                const rutas = ['controlembarques/planificacion-pi', 'puertos', 'solicitudesinternas'];
                const respuestas = await Promise.all(rutas.map((ruta) => fetch(`${urlApiProd}/${ruta}`, encabezados)));
                const [pis, puertos, internas] = await Promise.all(respuestas.map((res) => {
                    if (!res.ok) throw new Error('No se pudieron cargar los datos de la solicitud');
                    return res.json();
                }));
                if (activo) {
                    setDatos({ pis, puertos, internas });
                    // La PI preseleccionada llega con todo su saldo disponible
                    const inicial = idProformaInicial ? pis.find((p) => p.id_proforma === idProformaInicial) : null;
                    if (inicial && inicial.hc !== null && libreDe(inicial) > 0) {
                        setSeleccion([{ id_proforma: inicial.id_proforma, hc: String(libreDe(inicial)) }]);
                    }
                }
            } catch (e) {
                if (activo) setErrorCarga(e.message);
            } finally {
                if (activo) setCargando(false);
            }
        };
        cargar();
        return () => { activo = false; };
    }, [idProformaInicial]);

    const porId = useMemo(() => new Map(datos.pis.map((pi) => [pi.id_proforma, pi])), [datos.pis]);

    const filas = useMemo(
        () => seleccion.map((s) => ({ ...porId.get(s.id_proforma), hcPedir: Number(s.hc) || 0, hcTexto: s.hc })),
        [seleccion, porId]
    );
    const referencia = filas[0];

    // La primera PI fija cliente y destino; las siguientes deben compartirlos
    const candidatas = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        const elegidas = new Set(seleccion.map((s) => s.id_proforma));
        return datos.pis
            .filter((pi) => pi.hc !== null && libreDe(pi) > 0 && !elegidas.has(pi.id_proforma))
            .filter((pi) => !referencia || (pi.id_cliente === referencia.id_cliente && pi.id_puerto === referencia.id_puerto))
            .filter((pi) => !q || [pi.id_pi, pi.cliente, pi.producto, pi.pod].filter(Boolean).join(' ').toLowerCase().includes(q))
            .slice(0, MAX_RESULTADOS);
    }, [datos.pis, seleccion, referencia, busqueda]);

    const totalHc = filas.reduce((suma, f) => suma + f.hcPedir, 0);
    const mercado = referencia ? datos.puertos.find((p) => Number(p.id_puerto) === Number(referencia.id_puerto))?.pais : null;

    // ETD: el más próximo entre las PI elegidas, salvo que el usuario lo cambie
    const etdSugerido = useMemo(() => {
        const fechas = filas.map((f) => f.etd_objetivo).filter(Boolean).sort();
        return fechas[0] || '';
    }, [filas]);
    const etdValor = etdEditado ? etd : etdSugerido;

    // Free time: el último usado para ese cliente y destino, salvo que el usuario lo cambie
    const freeTimeSugerido = useMemo(() => {
        if (!referencia) return '';
        const previas = datos.internas
            .filter((s) => s.id_cliente === referencia.id_cliente && Number(s.id_puerto_destino) === Number(referencia.id_puerto))
            .sort((a, b) => b.id_solicitudinterna - a.id_solicitudinterna);
        return previas[0]?.freetimedestino || '';
    }, [datos.internas, referencia]);
    const freeTimeValor = freeTimeEditado ? freeTime : freeTimeSugerido;

    const hcValidos = filas.length > 0 && filas.every((f) => Number.isInteger(f.hcPedir) && f.hcPedir >= 1 && f.hcPedir <= libreDe(f));
    const puedeGuardar = hcValidos && !!etdValor && !!String(freeTimeValor).trim() && !guardando;
    const sinOc = filas.filter((f) => (f.ocs || []).length === 0);

    const agregarPi = (pi) => {
        setSeleccion((actual) => [...actual, { id_proforma: pi.id_proforma, hc: String(Math.min(libreDe(pi), 1)) }]);
        setBusqueda('');
        setListaAbierta(false);
    };
    const quitarPi = (id) => setSeleccion((actual) => actual.filter((s) => s.id_proforma !== id));
    const cambiarHc = (id, valor) => setSeleccion((actual) => actual.map((s) => (s.id_proforma === id ? { ...s, hc: valor } : s)));
    const agregarOtra = () => {
        setListaAbierta(true);
        if (inputBusqueda.current) inputBusqueda.current.focus();
    };

    const guardar = async () => {
        setErrorGuardado('');
        setGuardando(true);
        try {
            const token = localStorage.getItem('token');
            const respuesta = await fetch(`${urlApiProd}/controlembarques/solicitud-interna`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    pis: filas.map((f) => ({ id_proforma: f.id_proforma, cantidad_hc: f.hcPedir })),
                    etd: etdValor,
                    freetimedestino: String(freeTimeValor).trim(),
                    tipo_bl: tipoBl,
                    requerimientos,
                }),
            });
            const resultado = await respuesta.json().catch(() => ({}));
            if (!respuesta.ok) throw new Error(resultado.error || 'No se pudo crear la solicitud interna');
            if (onGuardada) onGuardada(resultado);
            cerrar();
        } catch (e) {
            setErrorGuardado(e.message);
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal show onHide={cerrar} backdrop="static" size="xl" centered scrollable>
            <Modal.Body className="p-0">
                <div className="nsi-cabecera">
                    <div className="ui-eyebrow">Comercial · Paso 1 de 2</div>
                    <h4 className="ui-title mb-1">Nueva solicitud interna</h4>
                    <p className="ui-descripcion mb-0">
                        Se arma desde una o más PI del mismo cliente y puerto destino. Lo que ya está en la PI no se vuelve a digitar.
                    </p>
                </div>

                {cargando && <div className="ce-muted p-4">Cargando PI…</div>}
                {errorCarga && <div className="alert alert-danger m-4">{errorCarga}</div>}

                {!cargando && !errorCarga && (
                    <div className="row g-0">
                        <div className="col-lg-8 p-4">
                            <h6 className="nsi-seccion">1. PI / Contrato</h6>
                            <div className="position-relative mb-2">
                                <label className="ce-filtro w-100">
                                    Buscar PI con saldo
                                    <input
                                        ref={inputBusqueda}
                                        type="text"
                                        className="w-100"
                                        autoComplete="off"
                                        placeholder="Ej: CTS 102-26"
                                        value={busqueda}
                                        onChange={(e) => { setBusqueda(e.target.value); setListaAbierta(true); }}
                                        onFocus={() => setListaAbierta(true)}
                                        onBlur={() => setTimeout(() => setListaAbierta(false), 150)}
                                    />
                                </label>
                                {listaAbierta && (
                                    <div className="list-group position-absolute w-100 shadow" style={{ top: '100%', zIndex: 1060, maxHeight: 240, overflowY: 'auto' }}>
                                        {candidatas.length > 0 ? candidatas.map((pi) => (
                                            <button
                                                key={pi.id_proforma}
                                                type="button"
                                                className="list-group-item list-group-item-action text-start"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => agregarPi(pi)}
                                            >
                                                <span className="ce-mono">{pi.id_pi}</span>
                                                <span className="ce-muted"> · {pi.cliente} → {pi.pod} · saldo {libreDe(pi)} HC</span>
                                            </button>
                                        )) : (
                                            <div className="list-group-item ce-muted">No hay PI con saldo disponible para esta búsqueda.</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {filas.length > 0 && (
                                <div className="table-responsive">
                                    <table className="ce-tabla">
                                        <thead>
                                            <tr>
                                                <th>PI</th>
                                                <th>Producto</th>
                                                <th>OC</th>
                                                <th>Saldo HC</th>
                                                <th>HC a pedir</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filas.map((f) => (
                                                <tr key={f.id_proforma}>
                                                    <td className="ce-mono">{f.id_pi}</td>
                                                    <td>{f.producto || '—'}</td>
                                                    <td className="ce-mono">{f.ocs.length > 0 ? f.ocs.map((oc) => oc.id_oc).join(' · ') : 'Sin OC'}</td>
                                                    <td className="ce-mono">{libreDe(f)}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={libreDe(f)}
                                                            step="1"
                                                            className="form-control form-control-sm"
                                                            style={{ width: 80 }}
                                                            value={f.hcTexto}
                                                            onChange={(e) => cambiarHc(f.id_proforma, e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <button type="button" className="ce-accion" onClick={() => quitarPi(f.id_proforma)} aria-label={`Quitar ${f.id_pi}`}>
                                                            Quitar
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {referencia && (
                                <>
                                    <div className="ce-muted my-2">
                                        El buscador solo muestra PI de {referencia.cliente} → {referencia.pod}, porque la solicitud ya tiene ese cliente y destino.
                                    </div>
                                    <button type="button" className="ui-btn ui-btn-secondary" onClick={agregarOtra}>
                                        + Agregar otra PI del mismo cliente y destino
                                    </button>
                                </>
                            )}

                            <h6 className="nsi-seccion">2. Heredado de la PI</h6>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="ce-filtro w-100">Cliente
                                        <input type="text" className="w-100" value={referencia?.cliente || ''} placeholder="—" readOnly />
                                    </label>
                                </div>
                                <div className="col-md-4">
                                    <label className="ce-filtro w-100">Puerto destino
                                        <input type="text" className="w-100" value={referencia?.pod || ''} placeholder="—" readOnly />
                                    </label>
                                </div>
                                <div className="col-md-4">
                                    <label className="ce-filtro w-100">Mercado
                                        <input type="text" className="w-100" value={mercado || ''} placeholder="—" readOnly />
                                    </label>
                                </div>
                            </div>

                            <h6 className="nsi-seccion">3. Datos de la solicitud</h6>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="ce-filtro w-100">ETD objetivo
                                        <DateInput
                                            name="etd"
                                            value={etdValor}
                                            onChange={(e) => { setEtd(e.target.value); setEtdEditado(true); }}
                                            required
                                        />
                                    </label>
                                </div>
                                <div className="col-md-4">
                                    <label className="ce-filtro w-100">Free time destino (días)
                                        <input
                                            type="text"
                                            className="w-100"
                                            value={freeTimeValor}
                                            onChange={(e) => { setFreeTime(e.target.value); setFreeTimeEditado(true); }}
                                            placeholder="Ej: 14"
                                        />
                                    </label>
                                </div>
                                <div className="col-md-4">
                                    <label className="ce-filtro w-100">Total HC (suma de las PI)
                                        <input type="text" className="w-100" value={totalHc || ''} placeholder="—" readOnly />
                                    </label>
                                </div>
                                <div className="col-md-6">
                                    <label className="ce-filtro w-100">Tipo de contenedor
                                        <select className="w-100" defaultValue={TIPO_CONTENEDOR}>
                                            <option value={TIPO_CONTENEDOR}>{TIPO_CONTENEDOR}</option>
                                        </select>
                                    </label>
                                </div>
                                <div className="col-md-6">
                                    <div className="ce-filtro">Tipo de BL</div>
                                    <div className="d-flex gap-4 mt-1">
                                        {['BL Original', 'Express Release'].map((valor) => (
                                            <label key={valor} className="d-flex align-items-center gap-2">
                                                <input type="radio" name="tipo_bl" value={valor} checked={tipoBl === valor} onChange={() => setTipoBl(valor)} />
                                                {valor}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="col-12">
                                    <label className="ce-filtro w-100">Requerimientos especiales
                                        <textarea
                                            className="w-100"
                                            rows={3}
                                            style={{ padding: '6px 8px', border: '1px solid #cbd7d1', borderRadius: 6 }}
                                            value={requerimientos}
                                            onChange={(e) => setRequerimientos(e.target.value)}
                                        />
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-4 p-4 nsi-lateral">
                            <h6 className="nsi-seccion mt-0">Resumen de saldos</h6>
                            {filas.length === 0 && <div className="ce-muted">Elija una PI para ver su saldo.</div>}
                            {filas.map((f) => (
                                <div key={f.id_proforma} className="nsi-resumen">
                                    <div className="ce-mono fw-bold mb-1">{f.id_pi}</div>
                                    <div className="nsi-fila"><span>HC contrato</span><strong>{f.hc}</strong></div>
                                    <div className="nsi-fila"><span>Embarcado</span><strong>{f.hc_embarcado}</strong></div>
                                    <div className="nsi-fila"><span>En booking</span><strong>{Math.max(f.hc_asignado - f.hc_embarcado, 0)}</strong></div>
                                    <div className="nsi-fila"><span>En otras solicitudes</span><strong>{f.hc_en_solicitudes}</strong></div>
                                    <div className="nsi-fila"><span>Esta solicitud</span><strong style={{ color: '#086b56' }}>{f.hcPedir}</strong></div>
                                    <div className="nsi-fila"><span>Saldo después</span><strong>{f.hc - f.hc_en_solicitudes - f.hcPedir}</strong></div>
                                </div>
                            ))}

                            <h6 className="nsi-seccion">Validaciones</h6>
                            <ul className="nsi-validaciones">
                                <li className={hcValidos || filas.length === 0 ? 'ok' : 'error'}>
                                    No se pueden pedir más HC que el saldo de cada PI.
                                </li>
                                <li className="ok">Todas las PI deben tener el mismo cliente y puerto destino.</li>
                            </ul>
                        </div>
                    </div>
                )}

            </Modal.Body>
            <Modal.Footer className="nsi-pie">
                {/* Avisos y errores: siempre visibles en el pie, a la izquierda de los botones */}
                <div className="nsi-avisos" role="status">
                    {errorGuardado && <div className="error">{errorGuardado}</div>}
                    {sinOc.map((f) => (
                        <div key={f.id_proforma} className="alerta">
                            {f.id_pi} aún no tiene OC vinculada. Puedes pedir el booking, pero no asignarlo hasta tener OC.
                        </div>
                    ))}
                </div>
                <button type="button" className="ui-btn ui-btn-secondary" onClick={cerrar} disabled={guardando}>Cancelar</button>
                <button type="button" className="ui-btn ui-btn-primary" onClick={guardar} disabled={!puedeGuardar}>
                    {guardando ? 'Guardando…' : 'Guardar solicitud'}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default NuevaSolicitudInterna;
